import moment from 'moment'
import type { Moment } from 'moment'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import { ReportType } from '@/app/constants/reportType'
import type { DatePickerProps, DatePickerRangeProps } from '@/modules/drivers/types'
import { zustandPlugin } from '@/modules/shared/openreplay'
import type { AlertModel } from '@/app/models/alert/alertModel'

const toDatePickerProps = (m: Moment): DatePickerProps => ({
  fullDate: m.format('YYYY-MM-DD'),
  year: m.year(),
  month: m.month() + 1,
  day: m.date(),
})

const getDefaultDateRange = (): DatePickerRangeProps => {
  const now = moment()
  const monthAgo = moment().subtract(1, 'month')
  return {
    startDate: toDatePickerProps(monthAgo),
    endDate: toDatePickerProps(now),
  }
}

type State = {
  amount: number
  upcoming: boolean
  onlyVisible: boolean
  filteredTypes: number[]
  showAddAlert: boolean
  onPrint: () => void
  dateRange: DatePickerRangeProps
  selectedAlerts: number[]
  reportType: ReportType
  showOnlyUpComing: boolean
  canEditAlerts: boolean
  alerts: AlertModel[]
  editedAlert: AlertModel | null
}

type Setter<T> = React.Dispatch<React.SetStateAction<T>>

type Actions = {
  setAmount: Setter<number>
  setUpcoming: (upcoming: boolean) => void
  setOnlyVisible: (onlyVisible: boolean) => void
  setFilteredTypes: (types: number[]) => void
  setShowAddAlert: Setter<boolean>
  setOnPrint: (callback: () => void) => void
  setDateRange: (range: DatePickerRangeProps) => void
  setSelectedAlerts: Setter<number[]>
  setReportType: (type: ReportType) => void
  setShowOnlyUpComing: Setter<boolean>
  setCanEditAlerts: Setter<boolean>
  setAlerts: (alerts: AlertModel[]) => void
  setEditedAlert: (alert: AlertModel | null) => void
}

const makeSetter =
  <K extends keyof State>(
    set: (partial: Partial<State>, replace?: boolean, action?: string) => void,
    get: () => State,
    key: K,
    actionName: string,
  ): Setter<State[K]> =>
  (updater) => {
    const prev = get()[key]
    const next =
      typeof updater === 'function'
        ? (updater as (p: State[K]) => State[K])(prev)
        : updater
    set({ [key]: next } as Partial<State>, false, actionName)
  }

export const useTimetableStore = create<State & Actions>()(
  devtools(
    zustandPlugin((set, get) => ({
      amount: 0,
      upcoming: true,
      onlyVisible: true,
      filteredTypes: [],
      showAddAlert: false,
      onPrint: () => {},
      dateRange: getDefaultDateRange(),
      selectedAlerts: [],
      reportType: ReportType.ALL_ALERTS,
      showOnlyUpComing: true,
      canEditAlerts: false,
      alerts: [],
      editedAlert: null,
      setAlerts: (alerts: AlertModel[]) => set({ alerts }),
      setEditedAlert: (editedAlert: AlertModel | null) =>
        set({ editedAlert }),
      setAmount: makeSetter(set, get, 'amount', 'setAmount'),
      setUpcoming: (upcoming) =>
        set({ upcoming }, false, 'setUpcoming'),
      setOnlyVisible: (onlyVisible) =>
        set({ onlyVisible }, false, 'setOnlyVisible'),
      setFilteredTypes: (filteredTypes) =>
        set({ filteredTypes }, false, 'setFilteredTypes'),
      setShowAddAlert: makeSetter(set, get, 'showAddAlert', 'setShowAddAlert'),
      setOnPrint: (onPrint) => set({ onPrint }, false, 'setOnPrint'),
      setDateRange: (dateRange) =>
        set({ dateRange }, false, 'setDateRange'),
      setSelectedAlerts: makeSetter(
        set,
        get,
        'selectedAlerts',
        'setSelectedAlerts',
      ),
      setReportType: (reportType) =>
        set({ reportType }, false, 'setReportType'),
      setShowOnlyUpComing: makeSetter(
        set,
        get,
        'showOnlyUpComing',
        'setShowOnlyUpComing',
      ),
      setCanEditAlerts: makeSetter(
        set,
        get,
        'canEditAlerts',
        'setCanEditAlerts',
      ),
    }), 'timetable'),
    { name: 'EasyTacho', store: 'timetable' },
  ),
)

export const useTimetable = () =>
  useTimetableStore(
    useShallow((s) => ({
      amount: s.amount,
      upcoming: s.upcoming,
      setAmount: s.setAmount,
      onlyVisible: s.onlyVisible,
      filteredTypes: s.filteredTypes,
      setUpcoming: s.setUpcoming,
      setOnlyVisible: s.setOnlyVisible,
      setFilteredTypes: s.setFilteredTypes,
      showAddAlert: s.showAddAlert,
      onPrint: s.onPrint,
      setShowAddAlert: s.setShowAddAlert,
      setOnPrint: s.setOnPrint,
      setDateRange: s.setDateRange,
      dateRange: s.dateRange,
      selectedAlerts: s.selectedAlerts,
      setSelectedAlerts: s.setSelectedAlerts,
      reportType: s.reportType,
      setReportType: s.setReportType,
      showOnlyUpComing: s.showOnlyUpComing,
      setShowOnlyUpComing: s.setShowOnlyUpComing,
      canEditAlerts: s.canEditAlerts,
      setCanEditAlerts: s.setCanEditAlerts,
      editedAlert: s.editedAlert,
      setEditedAlert: s.setEditedAlert,
    })),
  )
